/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  atom,
  noop,
  onCtxAbort,
  sleep,
  toAbortError,
  withAssign,
} from '@reatom/framework'

import { AsyncStateN, Equivalence } from '@blade/std-lib'
import { Duration, Result } from '@blade/std-lib'
import { TimeoutError } from '@root/shared/core/Error/Error'

import type { AbortError, Atom, Ctx, CtxSpy } from '@reatom/framework'

export interface AsyncStateAtom<E, A>
  extends Atom<AsyncStateN.AsyncStateN<E, A>> {
  isSuccess: Atom<boolean>
  isError: Atom<boolean>
  isLoading: Atom<boolean>
  isNotInitiated: Atom<boolean>
  map: <B>(fn: (ctx: CtxSpy, prev: A) => B) => AsyncStateAtom<E, B>
  unwrap: () => Atom<A | undefined>
}

export const reatomComputedAsyncState = <E, A>(
  fn: (ctx: CtxSpy) => AsyncStateN.AsyncStateN<E, A>,
  name?: string,
): AsyncStateAtom<E, A> => {
  const theAtom = atom(
    (ctx) => {
      return fn(ctx)
    },
    `async-state-computed.${name ?? 'unknown'}`,
  )

  const isSuccessAtom = atom((ctx) => {
    const state = ctx.spy(theAtom)
    return AsyncStateN.isSuccess(state)
  })
  const isErrorAtom = atom((ctx) => {
    const state = ctx.spy(theAtom)
    return AsyncStateN.isError(state)
  })
  const isLoadingAtom = atom((ctx) => {
    const state = ctx.spy(theAtom)
    return AsyncStateN.isLoading(state)
  })
  const isNotInitiatedAtom = atom((ctx) => {
    const state = ctx.spy(theAtom)
    return AsyncStateN.isNotInitiated(state)
  })

  const extendedAtom = theAtom.pipe(
    withAssign(() => ({
      isSuccess: isSuccessAtom,
      isError: isErrorAtom,
      isLoading: isLoadingAtom,
      isNotInitiated: isNotInitiatedAtom,
    })),
  )

  const map = <B>(fn: (ctx: CtxSpy, prev: A) => B) => {
    const mappedAtom = reatomComputedAsyncState((ctx) => {
      return ctx.spy(theAtom).pipe(AsyncStateN.map((prev) => fn(ctx, prev)))
    })
    return mappedAtom
  }

  const unwrap = () => {
    return atom((ctx) => {
      return ctx.spy(theAtom).value
    })
  }

  return extendedAtom.pipe(
    withAssign(() => ({
      map,
      unwrap,
    })),
  )
}

export const reatomAsyncState = <E, A>(
  init: AsyncStateN.AsyncStateN<E, A>,
  opts?: {
    eq?: Equivalence.Equivalence<A>
    name?: string
  },
) => {
  const eq = opts?.eq ?? Equivalence.strictEqual()
  const theAtom = atom<AsyncStateN.AsyncStateN<E, A>>(
    init,
    `async-state.${opts?.name ?? 'unknown'}`,
  )
  const computedAtom = reatomComputedAsyncState((ctx) => {
    return ctx.spy(theAtom)
  }, opts?.name)

  const setAction = action((ctx, value: AsyncStateN.AsyncStateN<E, A>) => {
    if (
      AsyncStateN.isSuccess(value) &&
      AsyncStateN.isSuccess(ctx.get(theAtom)) &&
      eq(value.value, value.value)
    ) {
      return
    }
    theAtom(ctx, value)
  })

  const setNotInitiatedAction = action((ctx) => {
    theAtom(ctx, AsyncStateN.notInitiated())
  })
  const setSuccessAction = action((ctx, value: A) => {
    const currentValue = ctx.get(theAtom)
    if (AsyncStateN.isSuccess(currentValue) && eq(currentValue.value, value)) {
      return
    }
    theAtom(ctx, AsyncStateN.success(value))
  })
  const setErrorAction = action((ctx, value: E) => {
    theAtom(ctx, AsyncStateN.error(value))
  })
  const setLoadingAction = action((ctx) => {
    theAtom(ctx, AsyncStateN.loading())
  })
  const setResultAction = action((ctx, result: Result.Result<A, E>) => {
    theAtom(ctx, AsyncStateN.fromResult(result))
  })

  return computedAtom.pipe(
    withAssign(() => ({
      set: setAction,
      setNotInitiated: setNotInitiatedAction,
      setSuccess: setSuccessAction,
      setError: setErrorAction,
      setLoading: setLoadingAction,
      setResult: setResultAction,
    })),
  )
}

reatomAsyncState.notInitiated = <E, A>(name?: string) => {
  return reatomAsyncState(AsyncStateN.notInitiated<E, A>(), { name })
}
reatomAsyncState.loading = <E, A>(name?: string) => {
  return reatomAsyncState(AsyncStateN.loading<E, A>(), { name })
}
reatomAsyncState.error = <E, A>(error: E, name?: string) => {
  return reatomAsyncState(AsyncStateN.error<E, A>(error), { name })
}
reatomAsyncState.success = <E, A>(value: A, name?: string) => {
  return reatomAsyncState(AsyncStateN.success<E, A>(value), { name })
}
reatomAsyncState.fromResult = <E, A>(
  result: Result.Result<A, E>,
  name?: string,
) => {
  return reatomAsyncState(AsyncStateN.fromResult<E, A>(result), { name })
}

type Args = {
  timeout?: Duration.Duration
}
export const waitAsyncStateAction = async <E, A>(
  ctx: Ctx,
  state: Atom<AsyncStateN.AsyncStateN<E, A>>,
  args?: Args,
): Promise<Result.Result<A, E | TimeoutError | AbortError>> => {
  const { timeout = Duration.seconds(30) } = args ?? {}

  let unsub = noop
  let unsubCtx = noop

  try {
    const promise = new Promise<Result.Result<A, E | AbortError>>((resolve) => {
      unsubCtx =
        onCtxAbort(ctx, () => {
          resolve(Result.fail(toAbortError('ctx aborted')))
        }) ?? noop

      unsub = ctx.subscribe(state, (value) => {
        if (AsyncStateN.isReady(value)) {
          resolve(AsyncStateN.toResult(value))
        }
      })
    })

    const timeoutPromise = sleep(Duration.toMillis(timeout)).then(
      () => new TimeoutError(),
    )

    const resulted = await Promise.race([promise, timeoutPromise])
    if (resulted instanceof TimeoutError) {
      return Result.fail(resulted)
    }
    return resulted
  } finally {
    unsub()
    unsubCtx()
  }
}
